import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import { getAdminFlashcards, editFlashcard, deleteFlashcard } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const Container = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
);

function FlashcardManager() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // {id, term, definition}

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ["admin-flashcards"],
    queryFn: getAdminFlashcards,
  });

  const startEdit = (c) =>
    setEditing({ id: c.id, term: c.term || "", definition: c.definition || "" });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    try {
      await editFlashcard(editing.id, {
        term: editing.term,
        definition: editing.definition,
      });
      toast.success("Flashcard updated");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin-flashcards"] });
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (c) => {
    if (c.is_core) {
      toast("Curated core cards stay in the Word Bank", {
        description: "You can edit the wording, but the core study deck cannot be deleted.",
      });
      return;
    }
    if (typeof window !== "undefined" && !window.confirm(`Delete flashcard "${c.term}"?`)) return;
    try {
      await deleteFlashcard(c.id);
      toast.success("Flashcard deleted");
      qc.invalidateQueries({ queryKey: ["admin-flashcards"] });
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const coreCount = cards.filter((card) => card.is_core).length;
  const aiCount = cards.length - coreCount;

  return (
    <div>
      <p className="text-sm font-medium text-[color:rgba(11,18,32,0.6)]">
        {isLoading
          ? "Loading..."
          : `${coreCount} curated core card(s)${aiCount ? ` · ${aiCount} AI-added card(s)` : ""}`}
      </p>
      <p className="mt-1 text-xs text-[color:rgba(11,18,32,0.5)]">
        Core cards ship with the study platform. AI-added cards are optional expansions from the reviewed
        question bank. You can edit any card; only AI-added cards can be deleted.
      </p>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
            <tr>
              <th className="px-4 py-3">Term</th>
              <th className="px-4 py-3">Deck</th>
              <th className="px-4 py-3">Definition</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cards.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[color:rgba(11,18,32,0.6)]">
                  No Word Bank cards are available yet. The curated core deck will appear after the next deployment starts.
                </td>
              </tr>
            )}
            {cards.map((c) => {
              const isEditing = editing && editing.id === c.id;
              return (
                <tr
                  key={c.id}
                  data-testid={STUDY.adminFlashcardRow}
                  className="border-t border-[color:rgba(10,27,51,0.08)] align-top"
                >
                  <td className="px-4 py-3 font-semibold text-[var(--ibp-ink)]">
                    {isEditing ? (
                      <input
                        value={editing.term}
                        onChange={(e) => setEditing((p) => ({ ...p, term: e.target.value }))}
                        className="w-full rounded-lg border border-[color:rgba(10,27,51,0.2)] px-2 py-1.5 text-sm"
                      />
                    ) : (
                      c.term
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[color:rgba(11,18,32,0.6)]">
                    <span
                      className={[
                        "rounded-full px-2 py-1 font-semibold",
                        c.is_core
                          ? "bg-[color:rgba(10,27,51,0.08)] text-[var(--ibp-ink)]"
                          : "bg-[color:rgba(214,162,30,0.16)] text-[#8a5d00]",
                      ].join(" ")}
                    >
                      {c.is_core ? "Curated" : "AI-added"}
                    </span>
                  </td>
                  <td className="max-w-md px-4 py-3 text-[color:rgba(11,18,32,0.82)]">
                    {isEditing ? (
                      <textarea
                        rows={3}
                        value={editing.definition}
                        onChange={(e) =>
                          setEditing((p) => ({ ...p, definition: e.target.value }))
                        }
                        className="w-full rounded-lg border border-[color:rgba(10,27,51,0.2)] px-2 py-1.5 text-sm"
                      />
                    ) : (
                      c.definition
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[color:rgba(11,18,32,0.55)]">
                    {c.source_reference || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <div className="flex gap-2">
                        <button
                          onClick={saveEdit}
                          data-testid={STUDY.adminFlashcardSave}
                          className="inline-flex items-center gap-1 rounded-lg bg-[var(--ibp-navy-950)] px-2.5 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                        >
                          <Save className="h-3.5 w-3.5" /> Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="inline-flex items-center gap-1 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]"
                        >
                          <X className="h-3.5 w-3.5" /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(c)}
                          data-testid={STUDY.adminFlashcardEdit}
                          className="inline-flex items-center gap-1 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        {!c.is_core ? (
                          <button
                            onClick={() => remove(c)}
                            data-testid={STUDY.adminFlashcardDelete}
                            className="inline-flex items-center gap-1 rounded-lg border border-[color:rgba(155,44,44,0.3)] px-2.5 py-1.5 text-xs font-semibold text-[#9b2c2c] hover:bg-[color:rgba(155,44,44,0.08)]"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        ) : null}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminFlashcards() {
  return (
    <div data-testid={STUDY.adminFlashcardsPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Word Bank Cards"
            subtitle="Manage the curated core deck and any optional AI-added cards."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate>
            <FlashcardManager />
          </AdminGate>
        </Container>
      </section>
    </div>
  );
}
